import { useQuery } from '@tanstack/react-query';
import { vehicleLabel, customerLabel, invoiceLabel } from './paramResolvers';

/**
 * Hook to fetch vehicle display label
 * Uses TanStack Query for caching and cancellation
 * 
 * @param id - Vehicle ID
 * @param teamId - Team ID
 * @returns Query result with label
 */
export function useVehicleLabel(id: string | null, teamId: string) {
  return useQuery({
    queryKey: ['vehicle-label', teamId, id],
    queryFn: () => vehicleLabel(id!, teamId),
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
  });
}

/**
 * Hook to fetch customer display label
 * Uses TanStack Query for caching and cancellation
 * 
 * @param id - Customer ID
 * @param teamId - Team ID
 * @returns Query result with label
 */
export function useCustomerLabel(id: string | null, teamId: string) {
  return useQuery({
    queryKey: ['customer-label', teamId, id],
    queryFn: () => customerLabel(id!, teamId),
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Hook to fetch invoice display label
 * Uses TanStack Query for caching and cancellation
 * 
 * @param id - Invoice ID
 * @param teamId - Team ID
 * @param type - Invoice type (sales or purchase)
 * @returns Query result with label
 */
export function useInvoiceLabel(
  id: string | null,
  teamId: string,
  type: 'sales' | 'purchase'
) {
  return useQuery({
    queryKey: ['invoice-label', teamId, type, id],
    queryFn: () => invoiceLabel(id!, teamId, type),
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}
